import {
  ChangeExtractionFieldRequestSchema,
  ENDPOINTS,
  type ChangeExtractionFieldRequest,
  type ChangeExtractionFieldResponse,
  type ListExtractionFieldsResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';

/**
 * The admin-defined extracted-field set — CLM-109 (FR-33, DR-09, NFR-17, CR-07).
 *
 * `listExtractionFields` reads the set, the revision history, the governed
 * names and the budget (`admin:configure`, no audit row); with an
 * `asOfRevision` it reads the set **as it was** at that revision — the DR-09
 * auditor path an old audit row points at. `changeExtractionField` adds, edits,
 * deactivates or reactivates one field (`admin:configure`); the
 * `expected_revision` it carries — the revision the page showed — is what makes
 * a stale page a `409 field_set_changed` rather than a silent overwrite of
 * another admin's change.
 *
 * The request is validated `.strict()` before anything is sent; every response
 * is parsed, not asserted, so an off-contract body — a gap in the history, more
 * active fields than the cap it states — is a `ZodError`, never a page showing a
 * field set that is not the field set.
 */

const LIST = ENDPOINTS['list-extraction-fields'];
const CHANGE = ENDPOINTS['change-extraction-field'];

export async function listExtractionFields(
  options: { readonly asOfRevision?: number; readonly signal?: AbortSignal } = {},
): Promise<ListExtractionFieldsResponse> {
  const raw = await apiGet<unknown>(LIST.path, {
    ...(options.asOfRevision === undefined ? {} : { query: { as_of_revision: String(options.asOfRevision) } }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  return LIST.response.parse(raw);
}

export async function changeExtractionField(
  request: ChangeExtractionFieldRequest,
  signal?: AbortSignal,
): Promise<ChangeExtractionFieldResponse> {
  const body = ChangeExtractionFieldRequestSchema.parse(request);
  const raw = await apiPost<unknown>(CHANGE.path, body, signal === undefined ? {} : { signal });
  return CHANGE.response.parse(raw);
}
